<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { UiText, UiIcon, UiChip, UiAvatar, UiInfoBox, UiTooltip } from "@shopexperts/shared";
import type { ExpertLevelAssignment } from "@shopexperts/legacy-api";
import { useAdminExpertLevelHistory } from "@/app/composables/expert-levels";
import { formatCommission, formatThreshold } from "@/app/composables/expert-levels/expertLevelForm";

const props = defineProps<{
  expertId: number | null | undefined;
}>();

const expertId = computed(() => props.expertId);
const hasExpert = computed(() => typeof expertId.value === "number" && expertId.value > 0);

const { assignments, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
  useAdminExpertLevelHistory(expertId, { enabled: hasExpert });

function isAdminSource(assignment: ExpertLevelAssignment): boolean {
  return assignment.assignment_source === "admin";
}

function sourceLabel(assignment: ExpertLevelAssignment): string {
  return isAdminSource(assignment) ? "Manual override" : "Auto-enrolled";
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function assignedByName(assignment: ExpertLevelAssignment): string {
  const user = assignment.assigned_by_user;
  if (!user) return "System";
  return user.name ?? user.email ?? `User #${user.id}`;
}

// Infinite scroll observer
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watch(sentinel, (el) => {
  if (observer) observer.disconnect();
  if (!el) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { rootMargin: "200px" },
  );
  observer.observe(el);
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="!hasExpert">
      <UiInfoBox variant="warning" title="No history available">
        <UiText as="span" variant="p-small">
          <span>This expert can't be resolved, so level history can't be loaded.</span>
        </UiText>
      </UiInfoBox>
    </div>

    <template v-else>
      <div class="flex flex-col gap-1">
        <UiText as="h3" variant="h6" class="font-semibold text-se-dark" text="Level history" />
        <UiText
          as="p"
          variant="p-small"
          class="text-se-gray-600"
          text="Every level the expert has held, newest first. Entries are append-only: auto-enrollment by gross sales and manual admin overrides both leave a permanent record."
        />
      </div>

      <!-- Error -->
      <UiInfoBox v-if="error" variant="error" title="Couldn't load history">
        <UiText as="span" variant="p-small">
          <span>Something went wrong loading the level history. Refresh to try again.</span>
        </UiText>
      </UiInfoBox>

      <!-- Loading -->
      <div v-else-if="isLoading" class="flex flex-col gap-3">
        <div v-for="n in 3" :key="n" class="h-24 animate-pulse rounded-xl bg-se-gray-100" />
      </div>

      <!-- Empty -->
      <div
        v-else-if="assignments.length === 0"
        class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-se-gray-300 py-16 text-center"
      >
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-se-gray-100">
          <UiIcon name="shopexperts-badge" class="h-6 w-6 text-se-gray-500" />
        </div>
        <UiText as="h4" variant="h6" class="font-semibold" text="No level history yet" />
        <UiText
          as="p"
          variant="p-small"
          class="text-se-gray-600"
          text="Once the expert is enrolled into a level — automatically or by an admin — it will appear here."
        />
      </div>

      <!-- Timeline -->
      <ul v-else class="font-archivo flex flex-col">
        <li
          v-for="(assignment, index) in assignments"
          :key="assignment.id"
          class="relative flex gap-3 sm:gap-4"
        >
          <!-- Rail -->
          <div class="flex flex-col items-center pt-3">
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-secondary"
              :class="isAdminSource(assignment)
                ? 'bg-se-dark text-se-white'
                : 'bg-se-success-light text-se-green'"
            >
              <UiIcon :name="isAdminSource(assignment) ? 'pencil' : 'repeat'" class="h-4 w-4" />
            </span>
            <span
              v-if="index !== assignments.length - 1"
              aria-hidden="true"
              class="mt-1.5 w-px flex-1 bg-se-gray-200"
            />
          </div>

          <!-- Card -->
          <article
            class="mb-3 min-w-0 flex-1 overflow-hidden rounded-xl border border-se-gray-200 bg-se-white transition-all duration-200 hover:border-se-gray-300 hover:shadow-[0_10px_28px_-22px_rgba(16,24,40,0.45)]"
          >
            <div class="flex flex-col gap-3 p-4">
              <!-- Header -->
              <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                <div class="min-w-0">
                  <div class="flex min-w-0 flex-wrap items-center gap-2">
                    <UiText
                      as="p"
                      variant="p"
                      :text="assignment.level.public_title"
                      class="min-w-0 font-semibold text-se-dark"
                    />
                    <UiChip
                      :text="sourceLabel(assignment)"
                      :variant="isAdminSource(assignment) ? 'dark' : 'success'"
                      size="xs"
                      rounded="full"
                    />
                  </div>
                  <UiTooltip :content="formatDateTime(assignment.assigned_at)">
                    <UiText
                      as="span"
                      variant="p-small"
                      :text="formatDateTime(assignment.assigned_at)"
                      class="text-se-gray-700"
                    />
                  </UiTooltip>
                </div>

                <div class="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                  <UiChip
                    :text="`${formatCommission(assignment.level.commission_rate_bps)} commission`"
                    variant="neutral"
                    size="xs"
                    rounded="full"
                  />
                  <UiChip
                    :text="`${formatThreshold(assignment.level.auto_enroll_threshold_gross_cents)} threshold`"
                    variant="plain"
                    size="xs"
                    rounded="full"
                  />
                </div>
              </div>

              <!-- Assigned by -->
              <div class="flex items-center gap-2">
                <UiAvatar
                  v-if="isAdminSource(assignment)"
                  :name="assignment.assigned_by_user?.name ?? undefined"
                  :url="assignment.assigned_by_user?.avatar ?? undefined"
                  size="xxs"
                  :responsive="false"
                  color-coded
                />
                <UiIcon v-else name="repeat" class="h-4 w-4 text-se-gray-500" />
                <UiText
                  as="span"
                  variant="caption-normal"
                  class="text-se-gray-600"
                  :text="isAdminSource(assignment)
                    ? `Assigned by ${assignedByName(assignment)}`
                    : 'Assigned automatically by gross-sales threshold'"
                />
              </div>

              <!-- Notes -->
              <div v-if="assignment.notes" class="rounded-md bg-se-gray-100 px-3 py-2">
                <UiText as="span" variant="caption" class="text-se-gray-500" text="Reason / notes" />
                <UiText as="p" variant="small" :text="assignment.notes" class="text-se-dark" />
              </div>
            </div>
          </article>
        </li>
      </ul>

      <div ref="sentinel" class="h-1" />

      <div v-if="isFetchingNextPage" class="py-3 text-center">
        <UiText as="p" variant="p-small" class="text-se-gray-500" text="Loading more…" />
      </div>
    </template>
  </div>
</template>
